import axios from 'axios';
import API from '../config/API';

// Tracked booking links: `?src=instagram`, `?src=qr`, a post's own code.
// The booking page remembers which link a client came in on (per salon, so a
// link for one salon is never credited on another's booking) and when, and
// the booking carries both. The server applies Beu's rule (the link counts
// unless an email tap or a delivered text came after it; 14 days) and counts
// visits per link for Performance. Reporting only: nothing here changes what
// the client can book or pay.
const KEY = 'beu-tracked-src';
const SRC_RE = /^[a-z0-9_-]{1,40}$/;
const WINDOW_MS = 14 * 24 * 60 * 60 * 1000;
const OBJECT_ID = /^[0-9a-fA-F]{24}$/;

const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || '{}') || {}; } catch (e) { return {}; } };
const write = (v) => { try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (e) { /* private mode: not remembered */ } };

/** Did they come from the group's brand page (it marks its location links)? */
export function viaBrand(search = (typeof window !== 'undefined' ? window.location.search : '')) {
    try { return new URLSearchParams(search).get('via') === 'brand'; } catch (e) { return false; }
}

/** The `src` on this page's address, cleaned, or null. */
export function srcFromUrl(search = (typeof window !== 'undefined' ? window.location.search : '')) {
    let raw = null;
    try { raw = new URLSearchParams(search).get('src'); } catch (e) { return null; }
    const s = String(raw || '').trim().toLowerCase();
    return SRC_RE.test(s) ? s : null;
}

/**
 * On landing: remember this link for this salon (the newest wins) and count
 * the visit once per salon, link and day in this browser. salonId must be the
 * salon's id (never a slug); without one nothing is kept.
 */
export function captureTrackedSource(salonId, { search, now = Date.now() } = {}) {
    if (!OBJECT_ID.test(String(salonId || ''))) return null;
    const src = srcFromUrl(search);
    if (!src) return null;
    const all = read();
    // Drop anything older than the window while we're here
    Object.keys(all).forEach((k) => { if (!all[k] || !(now - all[k].at < WINDOW_MS)) delete all[k]; });
    all[String(salonId)] = { src, at: now };
    write(all);
    const day = new Date(now).toISOString().slice(0, 10);
    const seenKey = `${KEY}-seen-${salonId}-${src}-${day}`;
    let seen = false;
    try { seen = !!sessionStorage.getItem(seenKey); sessionStorage.setItem(seenKey, '1'); } catch (e) { /* count it */ }
    if (!seen) {
        // From the brand page: a choice of this location, not a new landing
        // (the brand page counted the landing)
        const via = viaBrand(search) ? { via: 'brand' } : {};
        axios.post(API.ANALYTICS_LINK_VISIT, { salonId: String(salonId), src, ...via }, {
            headers: { 'Content-Type': 'application/json', authorization: 'Basic YmV1YXBwOmJldWFwcA==' },
        }).catch(() => { /* a missed count never blocks the page */ });
    }
    return { src, srcAt: now };
}

/** What a booking at this salon carries: { src, srcAt } while fresh, else {}. */
export function trackedSourceFor(salonId, now = Date.now()) {
    const hit = read()[String(salonId || '')];
    if (!hit || !SRC_RE.test(String(hit.src || '')) || !(now - hit.at < WINDOW_MS) || hit.at > now + 60000) return {};
    return { src: hit.src, srcAt: hit.at };
}

/**
 * A multi-location brand page: count the landing once per brand, link and day
 * in this browser, and give back the query its location links should carry
 * (the same src, marked as coming from the brand page).
 */
export function captureBrandLanding(brandSlug, { search, now = Date.now() } = {}) {
    const src = srcFromUrl(search);
    const slug = String(brandSlug || '').trim().toLowerCase();
    if (!src || !slug) return false;
    const day = new Date(now).toISOString().slice(0, 10);
    const seenKey = `${KEY}-brand-${slug}-${src}-${day}`;
    let seen = false;
    try { seen = !!sessionStorage.getItem(seenKey); sessionStorage.setItem(seenKey, '1'); } catch (e) { /* count it */ }
    if (!seen) {
        axios.post(API.ANALYTICS_LINK_VISIT, { brand: slug, src }, {
            headers: { 'Content-Type': 'application/json', authorization: 'Basic YmV1YXBwOmJldWFwcA==' },
        }).catch(() => { /* a missed count never blocks the page */ });
    }
    return true;
}

/**
 * The query a brand page's location links carry: the page's own query (kept
 * as it was), with a tracked src marked via=brand. No src: unchanged.
 */
export function brandLinkQuery(search = (typeof window !== 'undefined' ? window.location.search : '')) {
    const src = srcFromUrl(search);
    if (!src) return search || '';
    let params;
    try { params = new URLSearchParams(search); } catch (e) { return search || ''; }
    params.set('src', src);
    params.set('via', 'brand');
    return `?${params.toString()}`;
}
